import { Session } from '../types/Session';
import { Run } from '../types/Run';

export interface SessionRunTimeRange {
  start: string;
  end: string;
  tzLabel: string | null;
}

export type RunTimeMode = 'time' | 'datetime';

const TZ_OFFSET_RE = /([+-])(\d{2}):?(\d{2})$/;

// Append a `Z` to naive ISO strings so JavaScript parses them as UTC instead
// of the browser's local zone. Storage policy is UTC; any string without an
// explicit `Z` or `±HH:MM` is assumed UTC.
function ensureUtcIso(iso: string): string {
  if (/Z$/.test(iso)) return iso;
  if (TZ_OFFSET_RE.test(iso)) return iso;
  return `${iso}Z`;
}

function pickRunTimestamp(run: Run): string | null {
  return run.run_ts || null;
}

function readPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

export function formatRunWallClock(
  iso: string | null | undefined,
  timezone: string | null | undefined,
  mode: RunTimeMode = 'time',
): string {
  if (!iso) return '';

  const date = new Date(ensureUtcIso(iso));
  const isValidDate = !Number.isNaN(date.getTime());

  if (isValidDate && timezone) {
    try {
      const opts: Intl.DateTimeFormatOptions = mode === 'datetime'
        ? {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timezone,
        }
        : {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timezone,
        };
      const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(date);
      const hour = readPart(parts, 'hour');
      const minute = readPart(parts, 'minute');
      if (mode === 'datetime') {
        const year = readPart(parts, 'year');
        const month = readPart(parts, 'month');
        const day = readPart(parts, 'day');
        return `${year}-${month}-${day} ${hour}:${minute}`;
      }
      return `${hour}:${minute}`;
    } catch {
      // Falls through to ISO-slice if timezone is malformed.
    }
  }

  if (mode === 'datetime') {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    if (m) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
  }
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : iso;
}

// Return an ISO-shaped string (`yyyy-MM-ddTHH:mm:ss`, no offset) whose
// components are the wall-clock components in `timezone`. Designed for Plotly
// date axes: Plotly parses naive ISO strings without applying any timezone
// shift, so tick labels render exactly these components in every browser.
export function toRecordingZoneIso(date: Date, timezone: string | null | undefined): string {
  if (Number.isNaN(date.getTime())) return '';
  if (!timezone) return date.toISOString().replace(/Z$/, '');
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const year = readPart(parts, 'year');
    const month = readPart(parts, 'month');
    const day = readPart(parts, 'day');
    const hour = readPart(parts, 'hour') === '24' ? '00' : readPart(parts, 'hour');
    const minute = readPart(parts, 'minute');
    const second = readPart(parts, 'second');
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  } catch {
    return date.toISOString().replace(/Z$/, '');
  }
}

function shortOffsetForZone(timezone: string, sampleDate: Date): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(sampleDate);
    const name = readPart(parts, 'timeZoneName');
    return name || null;
  } catch {
    return null;
  }
}

function offsetFromIso(iso: string): string | null {
  if (/Z$/.test(iso)) return 'UTC';
  const m = TZ_OFFSET_RE.exec(iso);
  if (!m) return null;
  return `UTC${m[1]}${m[2]}:${m[3]}`;
}

function deriveSessionTimezone(runs: Run[]): string | null {
  for (const r of runs) {
    if (r.timezone) return r.timezone;
  }
  return null;
}

export function getSessionRunTimeRange(session: Session | null | undefined): SessionRunTimeRange | null {
  if (!session) return null;
  const runs: Run[] = [];
  for (const cfg of session.configs ?? []) {
    for (const run of cfg.runs ?? []) {
      if (pickRunTimestamp(run)) runs.push(run);
    }
  }
  if (runs.length === 0) return null;

  runs.sort((a, b) => {
    const ta = Date.parse(ensureUtcIso(pickRunTimestamp(a) as string));
    const tb = Date.parse(ensureUtcIso(pickRunTimestamp(b) as string));
    return ta - tb;
  });

  const firstIso = pickRunTimestamp(runs[0]) as string;
  const lastIso = pickRunTimestamp(runs[runs.length - 1]) as string;
  const timezone = deriveSessionTimezone(runs);

  const start = formatRunWallClock(firstIso, timezone, 'time');
  const end = formatRunWallClock(lastIso, timezone, 'time');
  if (!start || !end) return null;

  let tzLabel: string | null = null;
  if (timezone) {
    const firstDate = new Date(ensureUtcIso(firstIso));
    tzLabel = shortOffsetForZone(timezone, Number.isNaN(firstDate.getTime()) ? new Date() : firstDate);
  }
  if (!tzLabel) tzLabel = offsetFromIso(firstIso);

  return { start, end, tzLabel };
}
